import { Text } from "@ui-kitten/components";
import MainContainer from "components/MainContainer";
import { setSelectedSubscription } from "features/slices/reservationFlowSlice";
import { SubscriptionDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useFetchTenantHasTflProviderQuery } from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Pressable, View } from "react-native";
import {
  getUniqSubscriptions,
  sortVoucherSubsByUnusedVouchers,
} from "utils/subscriptionUtils";
import CapturedSchedule from "./CapturedSchedule";
import TflEventSearch from "./TflEventSearch";
import PlanPackageSelector from "./components/PlanPackageSelector";
import Spacer from "components/Spacer";

export default function Schedule() {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("");
  const { account } = useAccount();
  if (!account) return null;

  const { data: hasTflProvider } = useFetchTenantHasTflProviderQuery();
  const { selectedSubscription } = useAppSelector(
    (state) => state.reservationFlow
  );

  const uniqSubscriptions = useMemo(() => {
    return getUniqSubscriptions(account.subscriptions)
      .sort((a, b) => a.id - b.id) // sort subscriptions by id, looks hierarchically especially when there are child plans
      .sort(
        // https://github.com/FanRally/fanrally-core/issues/1271
        sortVoucherSubsByUnusedVouchers
      );
  }, [account.subscriptions]);

  const tabs = useMemo(() => {
    const result: { key: string; title: string }[] = [];
    if (!selectedSubscription) return result;

    if (hasTflProvider) {
      const tflTabName =
        selectedSubscription.planPackage.tflTabName || "Tickets";
      result.push({ key: "TFL Tab", title: tflTabName });
    }

    const planTabName = selectedSubscription.planPackage.tabName || "Top Picks";
    result.push({ key: "Plan Tab", title: planTabName });

    return result;
  }, [hasTflProvider, selectedSubscription]);

  useEffect(() => {
    if (tabs.length === 0) return;
    setActiveTab(tabs[0].key);
  }, [tabs]);

  // gets called when initial mount and user adds/upgrades another membership
  useEffect(() => {
    if (selectedSubscription === null) {
      dispatch(setSelectedSubscription(uniqSubscriptions[0]));
    }
  }, [account.subscriptions]);

  return (
    <MainContainer title="Schedule">
      <View className="p-2">
        {uniqSubscriptions.length > 1 && (
          <Spacer>
            <PlanPackageSelector
              selectedSubscription={selectedSubscription}
              uniqSubscriptions={uniqSubscriptions}
              onSelect={(selectedSubscription: SubscriptionDetails) =>
                dispatch(setSelectedSubscription(selectedSubscription))
              }
            />
          </Spacer>
        )}

        {tabs.length > 1 && (
          <View className="flex-row justify-around bg-white border-b border-gray-200">
            {tabs.map((tab) => (
              <Pressable
                key={tab.key}
                onPress={() => setActiveTab(tab.key)}
                className={`py-3 px-4 ${
                  activeTab === tab.key && "border-b-2 border-blue-500"
                }`}
              >
                <Text
                  className={`font-semibold text-base ${
                    activeTab === tab.key ? "text-black" : "text-gray-500"
                  }`}
                >
                  {tab.title}
                </Text>
              </Pressable>
            ))}
          </View>
        )}

        <View className="flex-1">
          {tabs.map((tab) => (
            <View
              className={`flex-1 ${activeTab === tab.key ? "flex" : "hidden"}`}
              key={tab.key}
            >
              {tab.key === "TFL Tab" && hasTflProvider && <TflEventSearch />}
              {tab.key === "Plan Tab" && <CapturedSchedule />}
            </View>
          ))}
        </View>
      </View>
    </MainContainer>
  );
}
